/**
 * The rules a report's filters obey, in one place for the screen and the
 * server (RPT-076, RPT-024).
 *
 * - `required`: answered before the report runs.
 * - `requiredGroup`: every filter in the group is required until ANY one of
 *   them is answered.
 * - `disables`: while a filter has a value, the filters it names are switched
 *   off — cleared on screen, ignored by the server, never required.
 * - `requiredWhen` / `visibleWhen`: required, or shown at all, only while
 *   another filter holds one of the named values. A hidden filter is off in
 *   the same way a disabled one is.
 * - `split`: a date range asked as two dates, each end required on its own.
 *
 * A date range is carried as `from`/`to`, whatever the filter is called —
 * unless it names keys of its own (`range`).
 */

export interface RuledFilter {
  readonly key: string
  readonly label?: string
  readonly control: string
  readonly required?: boolean
  readonly requiredGroup?: string
  readonly disables?: readonly string[]
  /** A date range under keys of its own (RPT-016). */
  readonly range?: { readonly from: string; readonly to: string }
  /** Required only while another filter holds one of these values (RPT-128). */
  readonly requiredWhen?: { readonly key: string; readonly values: readonly string[] }
  /** Shown only while another filter holds one of these values (REP3-005). */
  readonly visibleWhen?: { readonly key: string; readonly values: readonly string[] }
  /** A range asked as two dates, each with its own required mark (REP2-004). */
  readonly split?: {
    readonly fromLabel: string
    readonly toLabel: string
    readonly requireFrom?: boolean
    readonly requireTo?: boolean
    readonly toFollowsFrom?: boolean
  }
}

/** The two keys a date range travels under: its own, or `from`/`to`. */
export const rangeKeys = (filter: Pick<RuledFilter, 'range'>): { from: string; to: string } =>
  filter.range ?? { from: 'from', to: 'to' }

type Values = Readonly<Record<string, unknown>>

const present = (v: unknown): boolean =>
  v !== undefined && v !== null && v !== '' && v !== false

/** Whether this one filter has a value. */
export const filterAnswered = (filter: RuledFilter, values: Values): boolean => {
  if (filter.control !== 'dateRange') return present(values[filter.key])
  const keys = rangeKeys(filter)
  return present(values[keys.from]) || present(values[keys.to])
}

/** Whether a filter's value is one of these — a list value matches on any of its parts. */
const holds = (values: Values, rule: { readonly key: string; readonly values: readonly string[] }): boolean => {
  const on = values[rule.key]
  if (typeof on !== 'string' || on === '') return false
  return on.split(',').some((v) => rule.values.includes(v))
}

/** Whether the filter is on screen at all, given the other values (REP3-005). */
export const filterVisible = (filter: Pick<RuledFilter, 'visibleWhen'>, values: Values): boolean =>
  !filter.visibleWhen || holds(values, filter.visibleWhen)

/**
 * The keys switched off: hidden by another filter's value, or disabled by
 * whatever else is answered. A hidden filter's own `disables` do not count.
 */
export const disabledFilterKeys = (filters: readonly RuledFilter[], values: Values): Set<string> => {
  const out = new Set<string>()
  for (const f of filters) if (!filterVisible(f, values)) out.add(f.key)
  for (const f of filters) {
    if (out.has(f.key) || !f.disables?.length || !filterAnswered(f, values)) continue
    for (const key of f.disables) if (key !== f.key) out.add(key)
  }
  return out
}

/** Whether the report has anything it insists on before running. */
export const hasMandatoryFilters = (filters: readonly RuledFilter[]): boolean =>
  filters.some((f) => f.required || f.requiredGroup || f.requiredWhen)

/**
 * Whether a filter is (still) mandatory given the values so far: a plain
 * required one always is, a grouped one until its group is answered. Drives
 * the "Required" mark on screen.
 */
export const filterIsRequired = (filter: RuledFilter, filters: readonly RuledFilter[], values: Values): boolean => {
  if (disabledFilterKeys(filters, values).has(filter.key)) return false
  if (filter.required) return true
  if (filter.requiredWhen && holds(values, filter.requiredWhen)) return true
  if (!filter.requiredGroup) return false
  return !filters.some((f) => f.requiredGroup === filter.requiredGroup && filterAnswered(f, values))
}

/**
 * Which ends of a split date range are (still) mandatory: drives the two
 * required marks and the run check. A range that is not split answers with
 * either end, so this is only for `split` ones.
 */
export const rangeEndsRequired = (
  filter: RuledFilter, filters: readonly RuledFilter[], values: Values,
): { from: boolean; to: boolean } => {
  if (disabledFilterKeys(filters, values).has(filter.key)) return { from: false, to: false }
  const wanted = filterIsRequired(filter, filters, values)
  const split = filter.split
  const keys = rangeKeys(filter)
  return {
    from: wanted && split?.requireFrom !== false,
    to: (wanted && split?.requireTo !== false) || Boolean(split?.toFollowsFrom && present(values[keys.from])),
  }
}

/**
 * Filters that still need an answer — grouped ones listed together. A split
 * range missing one end is listed as that end (key and label), so the message
 * says "To date" rather than "Period".
 */
export const unansweredFilters = (filters: readonly RuledFilter[], values: Values): RuledFilter[] => {
  const off = disabledFilterKeys(filters, values)
  return filters.flatMap((f): RuledFilter[] => {
    if (off.has(f.key)) return []
    if (f.control === 'dateRange' && f.split) {
      const ends = rangeEndsRequired(f, filters, values)
      const keys = rangeKeys(f)
      const out: RuledFilter[] = []
      if (ends.from && !present(values[keys.from])) out.push({ ...f, key: keys.from, label: f.split.fromLabel })
      if (ends.to && !present(values[keys.to])) {
        // Asked for because a From date was given, not by the group: on its own.
        const { requiredGroup: _group, ...alone } = f
        out.push({ ...(filterIsRequired(f, filters, values) ? f : alone), key: keys.to, label: f.split.toLabel })
      }
      return out
    }
    return filterIsRequired(f, filters, values) && !filterAnswered(f, values) ? [f] : []
  })
}

/** "Item, Period or Item category" — how a group is named in a message. */
export const describeRequired = (missing: readonly RuledFilter[]): string => {
  const singles = missing.filter((f) => !f.requiredGroup).map((f) => (f.label ?? f.key).toLowerCase())
  const groups = new Map<string, string[]>()
  for (const f of missing) {
    if (!f.requiredGroup) continue
    const list = groups.get(f.requiredGroup) ?? []
    list.push((f.label ?? f.key).toLowerCase())
    groups.set(f.requiredGroup, list)
  }
  const parts = [
    ...singles,
    ...[...groups.values()].map((names) =>
      names.length === 1 ? names[0]! : `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`),
  ]
  return parts.join(' and ')
}

/**
 * A duration column's minutes as the reference shows them: `2 d 3 hr`,
 * `5 hr 10 min`, `25 min`.
 */
export const formatDuration = (minutes: unknown): string => {
  const n = Math.round(Number(minutes))
  if (minutes === null || minutes === undefined || minutes === '' || !Number.isFinite(n)) return ''
  const sign = n < 0 ? '-' : ''
  let left = Math.abs(n)
  const d = Math.floor(left / 1440)
  left -= d * 1440
  const h = Math.floor(left / 60)
  const m = left - h * 60
  const parts = [d ? `${d} d` : '', h ? `${h} hr` : '', !d && m ? `${m} min` : ''].filter(Boolean)
  return parts.length ? `${sign}${parts.join(' ')}` : '0 min'
}
